import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import en from "./en.json";
import ro from "./ro.json";
import { widgetStrings } from "../widgets/strings";

export const LANGUAGES = ["en", "ro"] as const;
export type Language = (typeof LANGUAGES)[number];

const prefKey = "cv.lang";

export function isLanguage(v: unknown): v is Language {
  return typeof v === "string" && (LANGUAGES as readonly string[]).includes(v);
}

/** The language chosen in the user menu on this browser, if any; it wins over the account's locale. */
export function chosenLanguage(): Language | undefined {
  try {
    const v = localStorage.getItem(prefKey);
    return isLanguage(v) ? v : undefined;
  } catch {
    return undefined;
  }
}

export function chooseLanguage(lang: Language): void {
  try {
    localStorage.setItem(prefKey, lang);
  } catch {
    // Storage blocked: the choice lasts for this page only.
  }
  void i18n.changeLanguage(lang);
}

function browserLanguage(): Language {
  const nav = typeof navigator !== "undefined" ? navigator.language.slice(0, 2) : "en";
  return isLanguage(nav) ? nav : "en";
}

export function initI18n(): typeof i18n {
  if (!i18n.isInitialized) {
    void i18n.use(initReactI18next).init({
      resources: {
        en: { translation: en, widget: widgetStrings.en },
        ro: { translation: ro, widget: widgetStrings.ro },
      },
      lng: chosenLanguage() ?? browserLanguage(),
      fallbackLng: "en",
      supportedLngs: LANGUAGES,
      interpolation: { escapeValue: false },
      returnNull: false,
    });
    i18n.on("languageChanged", (lng) => {
      document.documentElement.lang = lng;
    });
    document.documentElement.lang = i18n.language;
  }
  return i18n;
}

export default i18n;
